import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@amore/ui/components/tooltip'
import { Check, CircleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { formatDate, formatMoney, isOverdue, plural, relativeDue, TODAY } from '@/lib/format'
import { paymentMethodLabels } from '@/lib/labels'
import { useCanEdit } from '@/mock/store'
import type { Expense, Payment, WeddingData } from '@/mock/types'
import { type Column, DataTable, type TableGroup } from '@/patterns/data-table'
import { categoryLabel } from '@/screens/suppliers/model'
import { type BudgetFilter, expenseCategory, expenseTitle, matchesFilter } from './model'

export type PaymentRow = { payment: Payment; expense: Expense; title: string }

/** The page's filters, read for single payments: "K zaplacení" is an unpaid payment, not an unpaid item. */
function matches(row: PaymentRow, filter: BudgetFilter) {
  const { paid, dueDate } = row.payment
  if (filter === 'due') return !paid
  if (filter === 'overdue') return !paid && isOverdue(dueDate)
  if (filter === 'paid') return paid
  return matchesFilter(row.expense, filter)
}

const monthLabel = (month: string) => {
  const label = new Date(`${month}-01T12:00:00`).toLocaleDateString('cs-CZ', {
    month: 'long',
    year: 'numeric',
  })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

const sum = (rows: PaymentRow[]) => rows.reduce((total, row) => total + row.payment.amount, 0)
const payments = (count: number) => `${count} ${plural(count, 'platba', 'platby', 'plateb')}`

/**
 * "Časová osa": every payment of every item by due date, the wedding's cash
 * flow. Overdue first, then month by month, then undated, and the paid ones
 * folded away at the end. A payment is marked paid right in the row; the row
 * opens its item on the Platby tab.
 */
export function PaymentTimeline({
  data,
  filter,
  query,
  onOpen,
  onPay,
  empty,
}: {
  data: WeddingData
  filter: BudgetFilter
  query: string
  onOpen: (expenseId: string) => void
  onPay: (expenseId: string, paymentId: string) => void
  empty: ReactNode
}) {
  const canEdit = useCanEdit()
  const needle = query.trim().toLocaleLowerCase('cs')
  const visible = data.budget.expenses
    .flatMap((expense) =>
      expense.payments.map((payment) => ({ payment, expense, title: expenseTitle(expense, data) })),
    )
    .filter((row) => matches(row, filter))
    .filter(
      (row) =>
        !needle || `${row.title} ${row.payment.name}`.toLocaleLowerCase('cs').includes(needle),
    )
    .sort((a, b) => (a.payment.dueDate ?? '9999').localeCompare(b.payment.dueDate ?? '9999'))

  const open = visible.filter((row) => !row.payment.paid)
  const months = new Map<string, PaymentRow[]>()
  for (const row of open) {
    const due = row.payment.dueDate
    if (!due || isOverdue(due)) continue
    months.set(due.slice(0, 7), [...(months.get(due.slice(0, 7)) ?? []), row])
  }
  const group = (
    id: string,
    label: ReactNode,
    rows: PaymentRow[],
    extra: Partial<TableGroup<PaymentRow>> = {},
  ): TableGroup<PaymentRow>[] =>
    rows.length
      ? [
          {
            id,
            label,
            rows,
            meta: payments(rows.length),
            summary: { amount: formatMoney(sum(rows)) },
            ...extra,
          },
        ]
      : []
  const thisMonth = TODAY.slice(0, 7)
  const groups: TableGroup<PaymentRow>[] = [
    ...group(
      'overdue',
      <span className="flex items-center gap-1.5">
        <CircleAlert aria-hidden="true" className="size-4" />
        Po splatnosti
      </span>,
      open.filter((row) => isOverdue(row.payment.dueDate)),
      { tone: 'danger' },
    ),
    ...[...months].flatMap(([month, rows]) =>
      group(
        month,
        month === thisMonth ? `Tento měsíc · ${monthLabel(month)}` : monthLabel(month),
        rows,
      ),
    ),
    ...group(
      'undated',
      'Bez splatnosti',
      open.filter((row) => row.payment.dueDate === null),
    ),
    ...group(
      'paid',
      'Zaplacené',
      visible.filter((row) => row.payment.paid),
      { collapsible: true, defaultCollapsed: filter !== 'paid' },
    ),
  ]

  return (
    <DataTable
      aria-label="Platby podle splatnosti"
      columns={columns(data)}
      getRowId={(row) => row.payment.id}
      onRowClick={(row) => onOpen(row.expense.id)}
      rowMuted={(row) => row.payment.paid}
      rowTone={(row) =>
        !row.payment.paid && isOverdue(row.payment.dueDate) ? 'danger' : undefined
      }
      rowActions={
        canEdit
          ? (row) =>
              row.payment.paid ? null : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      appearance="ghost"
                      tone="neutral"
                      size="icon"
                      className="size-9"
                      onClick={() => onPay(row.expense.id, row.payment.id)}
                    >
                      <Check aria-hidden="true" className="size-4" />
                      <span className="sr-only">
                        Označit jako zaplacené: {row.title}, {row.payment.name}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Označit jako zaplacené</TooltipContent>
                </Tooltip>
              )
          : undefined
      }
      groups={groups}
      empty={empty}
    />
  )
}

function columns(data: WeddingData): Column<PaymentRow>[] {
  return [
    {
      key: 'due',
      header: 'Splatnost',
      width: '8.5rem',
      cell: ({ payment }) => {
        if (!payment.dueDate) return <span className="text-muted-foreground">—</span>
        const late = !payment.paid && isOverdue(payment.dueDate)
        return (
          <span className="flex flex-col">
            <span>{formatDate(payment.dueDate)}</span>
            {!payment.paid ? (
              <span
                className={
                  late ? 'text-xs text-danger-foreground' : 'text-xs text-muted-foreground'
                }
              >
                {relativeDue(payment.dueDate)}
              </span>
            ) : null}
          </span>
        )
      },
    },
    {
      key: 'payment',
      header: 'Platba',
      width: 'minmax(0,1fr)',
      primary: true,
      cell: ({ payment, expense, title }) => (
        <span className="block min-w-0">
          <span className="block truncate">
            {title} · {payment.name}
          </span>
          <span className="block truncate text-xs font-normal text-muted-foreground">
            {categoryLabel(expenseCategory(expense, data))}
          </span>
        </span>
      ),
    },
    {
      key: 'method',
      header: 'Způsob',
      width: '7rem',
      mobile: 'hide',
      cell: ({ payment }) => paymentMethodLabels[payment.method],
    },
    {
      key: 'amount',
      header: 'Částka',
      width: '8rem',
      align: 'end',
      numeric: true,
      cell: ({ payment }) => formatMoney(payment.amount),
    },
    {
      key: 'status',
      header: 'Stav',
      width: '8.5rem',
      cell: ({ payment }) =>
        payment.paid ? (
          <Badge tone="success">Zaplaceno</Badge>
        ) : isOverdue(payment.dueDate) ? (
          <Badge tone="danger">Po splatnosti</Badge>
        ) : (
          <Badge>K zaplacení</Badge>
        ),
    },
  ]
}
